/**
 * 第三階段：搭火車旅行——臺北（台北101、捷運、故宮）和花蓮（太魯閣、七星潭）。
 * 標示「英文練習」的句子在所有語言中都刻意保留英文。
 */
import type { Messages } from './en';

export const zhTWTrips: Pick<Messages, 'trips'> = {
  trips: {
    mapNames: { station: '火車站', taipei: '臺北', mrt: '捷運站', museum: '國立故宮博物院', hualien: '花蓮', hotel: '海景飯店' },
    zones: {
      station: '火車站', taipei: '臺北', tower101: '台北101', taipeiStation: '臺北車站', taipeiPark: '城市公園',
      mrt: '捷運站', museum: '國立故宮博物院', gorge: '太魯閣步道', taroko: '太魯閣峽谷', beach: '七星潭', hualien: '花蓮', hotel: '海景飯店',
    },
    objects: {
      stTicket: '售票窗口', stTrain: '火車', twElevator: '台北101觀景台', tpTrain: '臺北車站', tpNoodles: '牛肉麵店',
      tpCakes: '鳳梨酥店', ubike: 'YouBike 微笑單車租借站', mrtMap: '臺北捷運路線圖', mrtRules: '捷運規定', mrtRide: '捷運月台',
      muCabbage: '翠玉白菜', muMeat: '肉形石', muDing: '青銅鼎（毛公鼎）', muRuware: '汝窯水仙盆', muScroll: '清明上河圖',
      muCurio: '多寶格', muExit: '往捷運出口', hlHelmet: '安全帽租借', hlView: '燕子口觀景點', hlStones: '疊石頭',
      hlSea: '太平洋', hlMochi: '麻糬店', hlTrain: '花蓮車站',
      hlQingshui: '清水斷崖觀景台', hlNineTurns: '九曲洞', hlPancake: '炸彈蔥油餅', hlWonton: '扁食店', hlAmis: '阿美族豐年祭舞蹈', hlWhale: '賞鯨船', htDesk: '飯店櫃檯', htBed: '你的飯店床鋪',
    },
    npc: { stationClerk: '售票員', guide: '城市導遊', busker: '街頭藝人', docent: '博物館導覽員', ranger: '國家公園保育員', hiker: '登山客', mochiAuntie: '麻糬阿姨', captain: '船長', receptionist: '櫃檯人員' },

    station: {
      counter: 'Good morning! Where would you like to go today?',
      dest: { taipei: '臺北', hualien: '花蓮' },
      destHint: { taipei: '台北101、捷運和國立故宮博物院', hualien: '太魯閣峽谷和七星潭' },
      price: (n: number) => `${n} 枚金幣（來回）`,
      howAsk: '要怎麼說呢？（英文練習）',
      ask: ['One return ticket to {place}, please.', 'Ticket. {place}. Now.', 'I want to go, give me train.'],
      wrong: '售票員笑著說：「可以說得再有禮貌一點嗎？」',
      bought: (place: string) => `🎫 你買到一張往${place}的來回票。快去月台吧！`,
      poor: '你的金幣不夠買這張票。',
      tooLate: '今天最後一班火車已經開走了。明天早上再來吧！',
      haveTicket: (place: string) => `你已經有往${place}的車票了。月台就在剪票口後面。`,
      noTicket: '你要先買票喔——到售票窗口買一張吧。',
      board: '🚆 上火車',
      boardBack: '🚆 搭火車回家',
      notNow: '等一下',
      trainText: (place: string) => `往${place}的火車正停在月台等你，車門開了！`,
      backText: '回家的火車在等你了。準備好回去了嗎？',
    },
    ride: {
      title: '🚆 火車窗外',
      toTaipei: ['稻田在早晨的陽光下閃閃發亮……', '小鎮、廟宇和機車一閃而過……', '高高的大樓……還有台北101！'],
      toHualien: ['右邊升起一座座綠色的山……', '鑽進隧道……一出來就看到大海！', '灰色的懸崖直直落進藍色的太平洋……'],
      home: ['火車輕輕地搖啊搖……', '你看著窗外，有點想睡覺……', '快到家了！'],
      announce: (place: string) => `“The next station is ${place}.”`,
      skip: '⏩ 跳過',
      arrive: (place: string) => `歡迎來到${place}！`,
    },
    taipei: {
      elevator: '通往89樓觀景台的電梯是全世界最快的電梯之一：只要37秒！',
      goUp: '🛗 搭到頂樓（20 枚金幣）',
      poor: '上觀景台需要 20 枚金幣。',
      viewTitle: '🏙️ 台北101 · 89樓',
      viewText: [
        '整個城市都在你腳下：河流、橋、山，還有好小好小的車子。',
        '大樓中間掛著一顆巨大的金色圓球——那是「風阻尼器」。颱風和地震的時候，它能讓大樓保持穩定。',
      ],
      viewAsk: '英文練習：What keeps the tower steady?',
      viewOptions: ['A giant golden ball', 'A long rope', 'Big windows'],
      viewRight: '✅ 答對了！這個阻尼器重達 660 公噸。',
      viewWrong: '❌ 不太對喔——看看那顆金色圓球！',
      noodles: { vendor: 'Welcome! Beef noodle soup — spicy or clear broth?', ok: 'Clear broth, please. One small bowl.', bad: ['Noodles! Hurry!', 'Spicy, but not spicy, but spicy.'], done: '牛肉軟嫩、麵條Q彈，湯頭好濃郁。你覺得自己超有力氣！', price: 45 },
      cakes: { vendor: 'Our pineapple cakes are fresh today! Would you like a gift box?', ok: 'Yes, please. A small box for my family, thank you!', bad: ['Cake. Box. Give.', 'I want all of them for free.'], done: '一盒漂亮的鳳梨酥。家人一定會很喜歡！', price: 40 },
      ubike: 'YouBike 微笑單車：刷卡、牽車，騎到任何一個站都可以還車。',
      rent: '🚲 租一台腳踏車（5 枚金幣）',
      ride: '你騎上 YouBike 囉！記得：紅燈要停下來。',
      returnBike: '🚲 還車',
      returned: '你把腳踏車還回去了。嗶！',
      onlyHere: '離開前你先把 YouBike 還回去了。',
      guide: [
        '歡迎來到臺北！搭捷運是逛城市最方便的方法。',
        '一定要吃吃看牛肉麵——每個家庭都有自己的祕密食譜。',
        '從台北101觀景台可以看到城市四周的山。',
        '「請問捷運站怎麼走？」很簡單：過馬路，然後左轉！',
      ],
      busker: '一位街頭藝人用二胡拉著好聽的曲子，二胡是一種只有兩條弦的樂器。',
      listen: '🎶 聽音樂、一起拍手',
      listened: '你跟著拍手。街頭藝人笑著向你鞠躬！',
    },
    mrt: {
      map: '五顏六色的路線穿過整個城市：紅線、藍線、綠線、橘線、棕線……',
      mapQuiz: '英文練習：“Which line goes to the zoo?”',
      mapOptions: ['The brown line', 'The blue line', 'The bus'],
      mapRight: '✅ 沒錯！棕線（文湖線）可以到臺北市立動物園。',
      mapWrong: '嗯……再看一次路線圖吧！',
      rules: '捷運規定——用英文讀讀看！',
      rulesQs: [
        { q: 'Can you eat or drink on the MRT?', a: ['No, eating and drinking are not allowed.', 'Yes, a picnic is fine.', 'Only ice cream.'] },
        { q: 'Who are the dark blue priority seats for?', a: ['Elderly people, pregnant women, people with children or injuries.', 'Anyone who runs fast.', 'Only the driver.'] },
        { q: 'Where do you stand on the escalator?', a: ['On the right, so people can walk on the left.', 'In the middle, with my arms out.', 'I sit down on it.'] },
      ],
      rulesRight: '✅ 答對了！',
      rulesWrong: '❌ 再試一次！',
      rulesDone: '你對捷運規定熟得就像在地人一樣！',
      platform: '列車到站時，月台門才會打開。你要去哪裡呢？',
      toMuseum: '🏛️ 士林——國立故宮博物院',
      toTower: '🏙️ 台北101／世貿站',
      toXimen: '🛍️ 西門——西門町',
      toLongshan: '🏮 龍山寺——萬華',
      toCks: '🏯 中正紀念堂——自由廣場',
      toBeitou: '♨️ 新北投——北投溫泉',
      riding: '叮咚！“Please mind the gap.”',
    },
    museum: {
      intro: '英文練習：讀一讀每個英文說明牌。導覽員會問你一個問題。',
      items: {
        muCabbage: {
          label: 'Jadeite Cabbage. Carved from one piece of jade, half green and half white. Look closely: two little insects hide on the leaves!',
          q: 'What is hiding on the cabbage?',
          a: ['Two little insects', 'A cat', 'A golden coin'],
        },
        muMeat: {
          label: 'Meat-shaped Stone. It looks just like a piece of braised pork, but it is a stone called jasper. The skin even has tiny pores!',
          q: 'What is the “meat” really made of?',
          a: ['Stone', 'Real pork', 'Chocolate'],
        },
        muDing: {
          label: 'Bronze ding. A cooking pot with three legs, almost three thousand years old. It has a long text written inside.',
          q: 'How many legs does a ding have?',
          a: ['Three', 'Four', 'None'],
        },
        muRuware: {
          label: 'Ru ware basin. Sky-blue pottery with fine cracks in the glaze. Fewer than one hundred pieces exist in the whole world.',
          q: 'What colour is Ru ware?',
          a: ['Sky blue', 'Bright red', 'Black'],
        },
        muScroll: {
          label: 'Along the River During the Qingming Festival. A painting more than eleven metres long, full of boats, bridges, shops and thousands of tiny people.',
          q: 'What can you see in the painting?',
          a: ['Boats, bridges and tiny people', 'Only one big tree', 'A football match'],
        },
        muCurio: {
          label: 'Curio box. A small wooden box with secret drawers, made for an emperor to keep his tiny treasures.',
          q: 'Who was the box made for?',
          a: ['An emperor', 'A fisherman', 'A cat'],
        },
      },
      right: '✅ 讀得好！',
      wrong: '❌ 再讀一次說明牌吧！',
      seen: (n: number, total: number) => `已認識的寶物：${n}/${total}`,
      exit: '要搭捷運回市區嗎？',
      ride: '🚇 搭捷運',
      docent: [
        '歡迎來到國立故宮博物院。這些寶物有好幾百年——甚至好幾千年的歷史喔。',
        '拍照時請不要開閃光燈，強光會傷害古老的畫作。',
        '我們最有名的寶物？當然是翠玉白菜！大家都想看看上面的小蟲子！',
      ],
    },
    shuttle: {
      stop: '台灣好行——太魯閣線',
      ask: 'Excuse me, does this bus go to Taroko Gorge?',
      where: '你想去哪裡？',
      city: '🏙️ 花蓮車站・東大門・吉安',
      taroko: '⛰️ 太魯閣峽谷',
      qixingtan: '🌊 七星潭',
      riding: '公車沿著海岸公路蜿蜒前進……',
      here: '你已經在這裡了！',
    },
    hualien: {
      helmet: '峽谷裡可能會有落石。走步道的時候，每個人都要戴安全帽！',
      rent: '⛑️ 借一頂安全帽（免費）',
      wearing: '你戴上一頂亮黃色的安全帽。安全第一！',
      already: '你已經戴著安全帽了。',
      needHelmet: '⛑️ 保育員攔住你：「走步道請戴安全帽喔！」',
      view: '燕子口：懸崖上有好多小洞，燕子在裡面築巢。河水在下面轟隆轟隆地流著。',
      look: '🔭 仔細看看',
      looked: '燕子在小洞裡飛進飛出。河水是土耳其藍色的，因為裡面有大理石的細粉！',
      viewHelmet: '在這裡要戴安全帽喔。可以到遊客中心借一頂。',
      stones: '到處都是圓圓滑滑的小石頭。大家會把它們疊成小小的塔。',
      stack: '🪨 疊石頭',
      sea: '這是太平洋。往那邊走，下一塊陸地在……好遠好遠的地方！',
      listen: '🌊 聽海浪的聲音',
      listened: '嘩……唰唰……每一道浪都讓小石頭滾來滾去。好放鬆喔。',
      welcome: '花蓮歡迎您 — 歡迎來到花蓮！一邊是高山，一邊是太平洋。',
      qingshui: '清水斷崖：翠綠的高山直直落入湛藍的太平洋，高度將近一千公尺！懸崖上還開鑿了一條公路。',
      lookCliff: '🏔️ 沿著海岸看過去',
      lookedCliff: '靠近斷崖的海水是深藍色，靠近岸邊的是土耳其藍。下面有一條長長的白色浪花。',
      nineTurns: '九曲洞：步道像一條蛇一樣在大理石間蜿蜒。抬頭看——兩邊的峭壁幾乎要碰在一起了！',
      // English practice: food orders below.
      pancake: { vendor: 'Bomb scallion pancake! With an egg inside — spicy sauce or not?', ok: 'A little spicy sauce, please. One with an egg!', bad: ['Pancake. Egg. Quick.', 'Make it boom!'], done: '外酥內軟，一層一層的，裡面的蛋還是半熟的。好燙好燙——可是好好吃！', price: 35 },
      wonton: { vendor: 'Hualien wonton soup — thin skins, pork inside. Small or large bowl?', ok: 'A small bowl, please. Thank you!', bad: ['Soup! Big! Now!', 'Wonton, no soup, no wonton.'], done: '滑嫩的扁食泡在清爽的湯裡，再加上芹菜。花蓮人的最愛！', price: 40 },
      amis: '阿美族是臺灣人數最多的原住民族，夏天的豐年祭（Ilisin）會一起跳舞。大家手牽手圍成一個大圓圈，一起唱歌。',
      watchDance: '💃 一起看、一起拍手',
      danced: '一位阿姨笑著把你拉進圓圈裡！踏、踏、踢……你學會了這支舞的第一步。',
      whale: '賞鯨船從花蓮港出發。海豚一整年都住在這裡，有時候還會有鯨魚游過。',
      boat: (n: number) => `⛴️ 搭船出海（${n} 枚硬幣，1 小時）`,
      sightings: [
        '是飛旋海豚！好幾十隻在船邊跳躍、旋轉。大家都歡呼起來！',
        '一隻巨大的抹香鯨噴氣——噗～～！——潛下去之前還露出了尾巴。',
        '飛魚貼著浪花飛過，還有一家子瓶鼻海豚在船邊一起游。',
      ],
      seaFact: '英文練習：“Dolphins are mammals. They breathe air, just like us.”',
      hotel: {
        desk: 'Welcome to the Seaside Hotel! Would you like a room for tonight?',
        price: (n: number) => `🛏️ 訂一間房（${n} 枚硬幣）`,
        howAsk: '要怎麼問呢？（英文練習）',
        ask: ['I’d like a room for one night, please.', 'Room. Now. One.', 'Give me the biggest room for free.'],
        wrong: '櫃檯人員微笑著說：「可以再問得更有禮貌一點嗎？」',
        booked: '🔑 這是你的鑰匙：302 號房，海景房喔。今晚睡這裡，明天再搭火車回家！',
        already: '你今晚已經有房間了。302 號房就在右邊那扇門後面。',
        poor: '你的硬幣不夠訂房間。',
        bed: '軟軟的床鋪上是乾淨的白色床單，窗外傳來海浪的聲音。',
        sleep: '😴 去睡覺',
        noRoom: '這張床是飯店的。請先到櫃檯訂房。',
        wake: '🌅 花蓮早安！太陽正從太平洋上升起。你回家的火車票還可以用喔。',
      },
      mochi: { vendor: 'Hualien mochi! Peanut, sesame or red bean?', ok: 'Peanut, please. Can I have two?', bad: ['Mochi now.', 'All flavours, no money.'], done: '軟軟的、QQ的、甜甜的。這是花蓮人最愛的點心！', price: 25 },
      ranger: [
        '「太魯閣」在住在這裡的太魯閣族語言裡，意思是「雄偉又美麗」。',
        '請走在步道上，也要一直戴著安全帽喔。',
        '這裡的大理石已經有好幾百萬年了。河水一天一天慢慢地切出了這座峽谷。',
      ],
      hiker: [
        '我把整條步道都走完了！腳好痠，可是風景超值得的。',
        '你有看到吊橋嗎？走上去會有點晃——好好玩！',
        '在七星潭可以看到太陽從海上升起。',
      ],
    },
  },
};
